import dayjs from 'dayjs';
import dayjsTimezonePlugin from 'dayjs/plugin/timezone.js';
import dayjsUTCPlugin from 'dayjs/plugin/utc.js';
import { normalizeCountryCode } from './countries.ts';
import { currencyCodeSchema, type CurrencyCode } from './currencies.ts';

dayjs.extend(dayjsUTCPlugin);
dayjs.extend(dayjsTimezonePlugin);

export type LocalizationPreferences = {
  locale: string;
  timeZone: string;
};

export const DEFAULT_LOCALE = 'en-US';
export const DEFAULT_TIME_ZONE = 'UTC';
export const DEFAULT_LOCALIZATION_PREFERENCES: LocalizationPreferences = {
  locale: DEFAULT_LOCALE,
  timeZone: DEFAULT_TIME_ZONE,
};
export const INVALID_DATE_LABEL = '—';

type DateInput = Date | number | string;

export type DateFormatPreferences = {
  locale?: string | null;
  options?: Intl.DateTimeFormatOptions;
};

type InstantFormatPreferences = DateFormatPreferences & {
  timeZone?: string | null;
};

type ZonedDateTimeInput = {
  date?: string | null;
  time?: string | null;
  timeZone?: string | null;
};

const normalizeLocaleInput = (value: string) => value.trim().replaceAll('_', '-');

export const canonicalizeLocale = (value: string | null | undefined): string | null => {
  if (!value) {
    return null;
  }

  try {
    const [canonicalLocale] = Intl.getCanonicalLocales(normalizeLocaleInput(value));

    return canonicalLocale ?? null;
  } catch {
    return null;
  }
};

export const canonicalizeTimeZone = (value: string | null | undefined): string | null => {
  if (!value) {
    return null;
  }

  try {
    return new Intl.DateTimeFormat(DEFAULT_LOCALE, { timeZone: value.trim() }).resolvedOptions()
      .timeZone;
  } catch {
    return null;
  }
};

/**
 * The zone an event's wall times are in: its own when it is a valid IANA zone, else
 * `bookerTimezone` (the booker's zone, or the caller's default where it has no booker). Events
 * saved before AGI-108 may store '' for an unknown zone.
 */
export const eventTimezone = (
  event: { timezone?: string | null },
  bookerTimezone: string,
): string =>
  canonicalizeTimeZone(event.timezone) ?? canonicalizeTimeZone(bookerTimezone) ?? DEFAULT_TIME_ZONE;

export const resolveOfficeLocale = (countryCode: string | null | undefined): string => {
  if (!countryCode) {
    return DEFAULT_LOCALE;
  }

  return canonicalizeLocale(`en-${countryCode.toUpperCase()}`) ?? DEFAULT_LOCALE;
};

export const VENUE_LOCALES = ['en-GB', 'en-US'] as const;

export type VenueLocale = (typeof VENUE_LOCALES)[number];

const BRITISH_ENGLISH_COUNTRIES = [
  ['GB', 'the United Kingdom'],
  ['IM', 'the Isle of Man'],
  ['JE', 'Jersey'],
  ['GG', 'Guernsey'],
  ['GI', 'Gibraltar'],
] as const;

const BRITISH_ENGLISH_COUNTRY_CODES: ReadonlySet<string> = new Set(
  BRITISH_ENGLISH_COUNTRIES.map(([code]) => code),
);

const BRITISH_ENGLISH_COUNTRY_NAMES = new Map<string, string>(BRITISH_ENGLISH_COUNTRIES);

export const britishEnglishCountryName = (country: string): string =>
  BRITISH_ENGLISH_COUNTRY_NAMES.get(normalizeCountryCode(country) ?? '') ?? country;

export const venueLocaleFromCountry = (country: string | null | undefined): VenueLocale => {
  const normalizedCountry = normalizeCountryCode(country);
  if (normalizedCountry && BRITISH_ENGLISH_COUNTRY_CODES.has(normalizedCountry)) {
    return 'en-GB';
  }

  return 'en-US';
};

export type VenueMoneyFormat = {
  currencyCode: CurrencyCode;
  locale: VenueLocale;
};

export const resolveVenueMoneyFormat = (venue: {
  currencyCode?: string | null;
  country?: string | null;
}): VenueMoneyFormat | undefined => {
  const parsedCurrency = currencyCodeSchema.safeParse(venue.currencyCode);
  if (!parsedCurrency.success) {
    return undefined;
  }

  return {
    currencyCode: parsedCurrency.data,
    locale: venueLocaleFromCountry(venue.country),
  };
};

export const venueGroupLocale = (
  countries: ReadonlyArray<string | null | undefined>,
): VenueLocale => {
  const allBritish =
    countries.length > 0 && countries.every(country => venueLocaleFromCountry(country) === 'en-GB');

  return allBritish ? 'en-GB' : 'en-US';
};

const SUNDAY_FIRST_REGIONS = new Set(['US', 'CA', 'MX', 'BR', 'JP', 'KR', 'TW']);

const SUNDAY_FIRST_LANGUAGES = new Set(['en', 'ja', 'ko', 'pt']);

export const getWeekStartsOn = (locale: string | null | undefined): 0 | 1 => {
  const resolved = locale || DEFAULT_LOCALE;
  const region = resolved.split('-')[1]?.toUpperCase();
  if (region) {
    return SUNDAY_FIRST_REGIONS.has(region) ? 0 : 1;
  }

  const language = resolved.split('-')[0]?.toLowerCase();

  return language != null && SUNDAY_FIRST_LANGUAGES.has(language) ? 0 : 1;
};

export const normalizeLocalizationPreferences = ({
  locale,
  timeZone,
}: {
  locale?: string | null;
  timeZone?: string | null;
}): LocalizationPreferences => ({
  locale: canonicalizeLocale(locale) ?? DEFAULT_LOCALE,
  timeZone: canonicalizeTimeZone(timeZone) ?? DEFAULT_TIME_ZONE,
});

const createFormatter = (
  preferences: InstantFormatPreferences,
  additionalOptions?: Intl.DateTimeFormatOptions,
) => {
  const normalized = normalizeLocalizationPreferences(preferences);
  const defaultOptions = preferences.options ? undefined : additionalOptions;

  return new Intl.DateTimeFormat(normalized.locale, {
    ...defaultOptions,
    ...preferences.options,
    timeZone: normalized.timeZone,
  });
};

const parseDate = (value: DateInput): Date | null => {
  const date = value instanceof Date ? value : new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
};

const parseCalendarDate = (value: string | null | undefined): Date | null => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return null;
  }

  const date = new Date(`${value}T12:00:00.000Z`);
  if (Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== value) {
    return null;
  }

  return date;
};

const normalizeWallTime = (value: string | null | undefined): string | null => {
  const match = /^([01]\d|2[0-3]):([0-5]\d)(?::([0-5]\d)(?:\.\d{1,9})?)?$/.exec(
    value?.trim() ?? '',
  );
  if (!match) {
    return null;
  }

  const [, hours, minutes, seconds = '00'] = match;

  return `${hours}:${minutes}:${seconds}`;
};

const parseZonedDateTime = (value: string, timeZone: string) => {
  try {
    return dayjs.tz(value, 'YYYY-MM-DD HH:mm:ss', timeZone);
  } catch {
    return dayjs(null);
  }
};

export const getInstantCalendarDate = (
  value: DateInput,
  preferences: { timeZone?: string | null } = {},
): string | null => {
  const date = parseDate(value);
  if (!date) {
    return null;
  }

  const timeZone = canonicalizeTimeZone(preferences.timeZone) ?? DEFAULT_TIME_ZONE;

  // en-CA renders numeric dates as YYYY-MM-DD.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
};

const noonUtcCivilDate = /^(\d{4}-\d{2}-\d{2})T12:00:00\.000Z$/;

export const toDateInputValue = (value: string | null | undefined, timeZone?: string): string => {
  if (!value) {
    return '';
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }

  const civilDate = noonUtcCivilDate.exec(value)?.[1];
  if (civilDate) {
    return civilDate;
  }

  const calendarTimeZone = timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

  return getInstantCalendarDate(value, { timeZone: calendarTimeZone }) ?? '';
};

export const formatCalendarDate = (
  value: string,
  preferences: DateFormatPreferences = {},
): string => {
  const date = parseCalendarDate(value);
  if (!date) {
    return INVALID_DATE_LABEL;
  }

  return createFormatter(
    { ...preferences, timeZone: DEFAULT_TIME_ZONE },
    { dateStyle: 'medium' },
  ).format(date);
};

export const formatInstant = (
  value: DateInput,
  preferences: InstantFormatPreferences = {},
): string => {
  const date = parseDate(value);
  if (!date) {
    return INVALID_DATE_LABEL;
  }

  return createFormatter(preferences, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

const INSTANT_DATE_AND_TIME_OPTIONS = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
} as const satisfies Intl.DateTimeFormatOptions;

/** Calendar date plus clock time in one Intl format (locale-controlled punctuation). */
export const formatInstantDateAndTime = (
  value: DateInput,
  preferences: InstantFormatPreferences = {},
): string =>
  formatInstant(value, {
    ...preferences,
    options: {
      ...INSTANT_DATE_AND_TIME_OPTIONS,
      ...preferences.options,
    },
  });

export const formatZonedDateTime = (
  input: ZonedDateTimeInput,
  preferences: DateFormatPreferences = {},
): string => {
  // A missing or invalid source timezone still has a meaningful wall time, so
  // render it neutrally in UTC instead of hiding the value behind the invalid label.
  const timeZone = canonicalizeTimeZone(input.timeZone) ?? DEFAULT_TIME_ZONE;

  const date = typeof input.date === 'string' && parseCalendarDate(input.date) ? input.date : null;
  const time = normalizeWallTime(input.time);
  if (!date || !time) {
    return INVALID_DATE_LABEL;
  }

  const zonedDate = parseZonedDateTime(`${date} ${time}`, timeZone);
  if (!zonedDate.isValid()) {
    return INVALID_DATE_LABEL;
  }

  return createFormatter(
    { ...preferences, timeZone },
    { dateStyle: 'medium', timeStyle: 'short' },
  ).format(zonedDate.toDate());
};

export const formatVenueTime = (
  input: ZonedDateTimeInput,
  preferences: DateFormatPreferences = {},
): string =>
  formatZonedDateTime(input, {
    ...preferences,
    options: preferences.options ?? { timeStyle: 'short' },
  });

// Clock-only venue times (hours of operation, availability windows) must not be
// interpreted in a user or venue timezone — UTC has no DST, so every valid
// civil time exists. Use formatVenueTime when the source timezone is known.
const WALL_TIME_ANCHOR_DATE = '1970-01-01';

export const formatWallTime = (
  time: string | null | undefined,
  preferences: DateFormatPreferences = {},
): string =>
  formatZonedDateTime(
    { date: WALL_TIME_ANCHOR_DATE, time, timeZone: DEFAULT_TIME_ZONE },
    {
      ...preferences,
      options: preferences.options ?? { timeStyle: 'short' },
    },
  );

export const formatReservationTimeLabel = (time: string | null | undefined): string | null => {
  if (time == null || time === '') {
    return null;
  }

  const formatted = formatWallTime(time, {
    locale: DEFAULT_LOCALE,
    options: { hour: 'numeric', minute: '2-digit', hour12: true },
  });

  if (formatted === INVALID_DATE_LABEL) {
    return time;
  }

  return formatted;
};

export const formatInstantRange = (
  start: DateInput,
  end: DateInput,
  preferences: InstantFormatPreferences = {},
): string => {
  const startDate = parseDate(start);
  const endDate = parseDate(end);
  if (!startDate || !endDate) {
    return INVALID_DATE_LABEL;
  }

  return createFormatter(preferences, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).formatRange(startDate, endDate);
};

export const formatRelativeInstant = (
  value: DateInput,
  preferences: { locale?: string | null; now?: DateInput } = {},
): string => {
  const date = parseDate(value);
  const now = parseDate(preferences.now ?? Date.now());
  if (!date || !now) {
    return INVALID_DATE_LABEL;
  }

  const locale = canonicalizeLocale(preferences.locale) ?? DEFAULT_LOCALE;
  const elapsedSeconds = (date.getTime() - now.getTime()) / 1000;
  const units: ReadonlyArray<[Intl.RelativeTimeFormatUnit, number]> = [
    ['year', 31_536_000],
    ['month', 2_592_000],
    ['week', 604_800],
    ['day', 86_400],
    ['hour', 3600],
    ['minute', 60],
    ['second', 1],
  ];

  for (const [unit, seconds] of units) {
    if (Math.abs(elapsedSeconds) >= seconds || unit === 'second') {
      const valueForUnit = Math.round(elapsedSeconds / seconds);

      return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(valueForUnit, unit);
    }
  }

  return INVALID_DATE_LABEL;
};

const previousCalendarDay = (isoDate: string): string =>
  dayjs.utc(`${isoDate}T12:00:00.000Z`).subtract(1, 'day').format('YYYY-MM-DD');

/**
 * List timestamp: localized relative copy for today, localized "yesterday" for the
 * previous calendar day, else month+day. Pass the user's locale and timezone
 * (web: `useLocalization()`). Invalid input returns an empty string.
 */
export const formatCalendarRelativeTime = (
  value: DateInput,
  preferences: InstantFormatPreferences & { now?: DateInput } = {},
): string => {
  const date = parseDate(value);
  const now = parseDate(preferences.now ?? Date.now());
  if (!date || !now) {
    return '';
  }

  const timeZone = preferences.timeZone;
  const updatedDay = getInstantCalendarDate(date, { timeZone });
  const nowDay = getInstantCalendarDate(now, { timeZone });
  if (!updatedDay || !nowDay) {
    return '';
  }

  if (updatedDay === previousCalendarDay(nowDay)) {
    const locale = canonicalizeLocale(preferences.locale) ?? DEFAULT_LOCALE;

    return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(-1, 'day');
  }

  if (updatedDay === nowDay) {
    return formatRelativeInstant(date, { locale: preferences.locale, now });
  }

  return formatInstant(date, {
    locale: preferences.locale,
    timeZone,
    options: { month: 'short', day: 'numeric' },
  });
};
